import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useParams } from "react-router-dom";
// @ts-ignore
import { getNews } from "../../services/newsService";
// @ts-ignore
import { getNewCategories } from "../../services/newCategoryService";
// @ts-ignore
import { getHeroForCustomer } from "../../services/heroService";
// @ts-ignore
import { useAutoSEO } from '../../hooks/useSEOFromRoute';

interface NewsCategory {
  _id: string;
  name: string;
  slug: string;
  icon?: string;
}

interface NewsItem {
  _id: string;
  title: string;
  excerpt: string;
  content: string;
  newCategoryId?: NewsCategory;
  newCategory?: NewsCategory;
  author: string;
  readTime: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  slug: string;
  image: string;
}

interface HeroSection {
  id: string;
  page: string;
  badge?: {
    text: string;
    icon: string;
  };
  heading: string;
  subheading?: string;
  description?: string;
  backgroundImage?: string;
  buttons: Array<{
    text: string;
    url: string;
    actionType: 'modal' | 'url' | 'scroll';
    style: 'primary' | 'secondary' | 'outline';
    order: number;
    isVisible: boolean;
  }>;
  isActive: boolean;
}

interface NewsPageProps {
  categorySlug?: string;
}

export default function NewsPage({ categorySlug: propCategorySlug }: NewsPageProps) {
  // Auto fetch SEO from database based on route
  useAutoSEO();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{ categorySlug?: string }>();
  
  // Get category from prop, route param, or query param (priority order)
  const categorySlug = propCategorySlug || params.categorySlug || searchParams.get('category') || undefined;
  
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [newsData, setnewsData] = useState<NewsItem[]>([]);
  const [heroSection, setHeroSection] = useState<HeroSection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  
  // Mobile category dropdown state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // 1 featured + 2 secondary + 6 regular

  // Handle category change
  const handleCategoryChange = (categoryId: string, slug?: string) => {
    if (categoryId === "all") {
      navigate("/tin-tuc");
      setSelectedCategory("all");
    } else {
      navigate(`/tin-tuc-${slug}`);
      setSelectedCategory(categoryId);
    }
  };


   // Fetch data only once on mount
   useEffect(() => {
     const fetchData = async () => {
       try {
         setLoading(true);
         setError(null);

         const [newCategoriesResponse, newsResponse, heroResponse] = await Promise.all([
           getNewCategories(),
           getNews(),
           getHeroForCustomer('news')
         ]);

         const fetchedCategories = newCategoriesResponse.data?.categories || [];
         setCategories(fetchedCategories);
         setnewsData(newsResponse.data?.news || []);

         // Load hero section
         if (heroResponse.success && heroResponse.data) {
           const heroData = {
             ...heroResponse.data,
             isActive: heroResponse.data.isActive !== undefined ? heroResponse.data.isActive : true,
             buttons: heroResponse.data.buttons || []
           };
           setHeroSection(heroData);
         }
       } catch (error: any) {
         setError(error.message || "Có lỗi xảy ra");
       } finally {
         setLoading(false);
       }
     };

     fetchData();
   }, []);

   // Handle category from prop (without re-fetching)
   useEffect(() => {
     if (categorySlug && categories.length > 0) {
       const foundCategory = categories.find((cat: NewsCategory) => cat.slug === categorySlug);
       if (foundCategory) {
         setSelectedCategory(foundCategory._id);
       } else {
         setSelectedCategory("all");
       }
     } else {
       setSelectedCategory("all");
     }
   }, [categorySlug, categories]);

   // Trigger hero animation on mount
   useEffect(() => {
     const timer = setTimeout(() => {
       setHeroVisible(true);
     }, 100);
     return () => clearTimeout(timer);
   }, []);

   // Trigger cards animation after data loaded
   useEffect(() => {
     if (!loading) {
       // Small delay for smoother animation
       const timer = setTimeout(() => {
         setIsVisible(true);
       }, 300);
       return () => clearTimeout(timer);
     }
   }, [loading]);

  const filteredNews =
    selectedCategory === "all"
      ? newsData
      : newsData.filter(
          (news: NewsItem) =>
            (news.newCategory?._id || news.newCategoryId?._id) ===
            selectedCategory
        );

  // Pagination calculation
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNews = filteredNews.slice(startIndex, endIndex);

  // Reset page when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Reset and trigger animation when page or category changes
  useEffect(() => {
    // Only reset cards animations, keep hero visible
    setIsVisible(false);
    
    const cardsTimer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => {
      clearTimeout(cardsTimer);
    };
  }, [currentPage, selectedCategory]);

  // Scroll to top when page changes
  useEffect(() => {
    if (currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const getCategoryIcon = (slug: string) => {
    const icons: Record<string, string> = {
      all: "ri-apps-line",
      web: "ri-code-s-slash-line",
      mobile: "ri-smartphone-line",
      "iot-ai": "ri-robot-line",
      "cng-ngh": "ri-lightbulb-line",
      "kinh-doanh": "ri-bar-chart-box-line",
    };
    return icons[slug] || "ri-article-line";
  };


  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00c897] rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-[#00c897] rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            {/* Badge with animation - only on initial load */}
            {heroSection?.badge && (
              <div className={`inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                {heroSection.badge.icon && <i className={`${heroSection.badge.icon} mr-2`}></i>}
                <span className="text-[#00c897] text-sm font-semibold">
                  {heroSection.badge.text}
                </span>
              </div>
            )}
            {!heroSection?.badge && (
              <div className={`inline-block px-4 py-2 bg-[#00c897]/20 border border-[#00c897]/30 rounded-full mb-6 backdrop-blur-sm transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <span className="text-[#00c897] text-sm font-semibold">
                  Cập nhật liên tục
                </span>
              </div>
            )}
            
            {/* Title - always visible after initial load */}
            <h1 className={`text-5xl md:text-6xl font-extrabold text-white mb-6 transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: heroVisible ? '100ms' : '0ms' }}>
              {heroSection?.heading || "Tin Tức Công Nghệ"}
            </h1>
            
            {/* Description - always visible after initial load */}
            <p className={`text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed transition-all duration-700 ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: heroVisible ? '200ms' : '0ms' }}>
              {heroSection?.description || heroSection?.subheading || "Khám phá những xu hướng công nghệ mới nhất, bài viết chuyên sâu và kinh nghiệm thực tế từ các chuyên gia"}
            </p>
          </div>
        </div>

        {/* Wave Decoration */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <svg
            className="w-full h-16"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z"
              fill="white"
            ></path>
          </svg>
        </div>
      </section>

      {/* Loading indicator */}
      {loading && (
        <div className="py-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#00c897] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Đang tải tin tức...</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="py-24 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-red-500"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Có lỗi xảy ra</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-[#00c897] text-white font-semibold rounded-lg hover:bg-[#00b587] transition-colors"
            >
              Thử lại
            </button>
          </div>
        </div>
      )}

      {/* Category Filter - Desktop: Sticky Buttons, Mobile: Dropdown */}
      {!loading && !error && (
        <>
          {/* Mobile: Dropdown Filter */}
          <section className="md:hidden py-4 bg-white border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <button
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border-2 border-gray-300 hover:border-[#2a4b7c] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <i className={`${selectedCategory === "all" ? "ri-apps-line" : categories.find((c: any) => c._id === selectedCategory)?.icon || getCategoryIcon(categories.find((c: any) => c._id === selectedCategory)?.slug || "")} text-[#2a4b7c] text-lg`}></i>
                  <span className="font-semibold text-[#333]">
                    {selectedCategory === "all" 
                      ? "Tất cả" 
                      : categories.find((c: any) => c._id === selectedCategory)?.name || "Chọn danh mục"}
                  </span>
                  {selectedCategory !== "all" && (
                    <span className="px-2 py-0.5 bg-[#2a4b7c] text-white rounded-full text-xs">
                      {filteredNews.length}
                    </span>
                  )}
                </div>
                <i className={`ri-arrow-${mobileFilterOpen ? 'up' : 'down'}-s-line text-xl text-[#2a4b7c]`}></i>
              </button>

              {/* Dropdown Menu */}
              {mobileFilterOpen && (
                <div className="mt-3 bg-white rounded-lg border-2 border-gray-200 shadow-lg overflow-hidden">
                  <button
                    onClick={() => {
                      handleCategoryChange("all");
                      setMobileFilterOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 transition-colors ${
                      selectedCategory === "all"
                        ? "bg-[#2a4b7c] text-white"
                        : "hover:bg-gray-50 text-[#333]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <i className="ri-apps-line text-lg"></i>
                      <span className="font-semibold">Tất cả</span>
                    </div>
                    {selectedCategory === "all" && (
                      <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                        {newsData.length}
                      </span>
                    )}
                  </button>

                  {categories.map((category: any) => (
                    <button
                      key={category._id}
                      onClick={() => {
                        handleCategoryChange(category._id, category.slug);
                        setMobileFilterOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 transition-colors ${
                        selectedCategory === category._id
                          ? "bg-[#2a4b7c] text-white"
                          : "hover:bg-gray-50 text-[#333]"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <i className={`${category.icon || getCategoryIcon(category.slug)} text-lg`}></i>
                        <span className="font-semibold">{category.name}</span>
                      </div>
                      {selectedCategory === category._id && (
                        <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                          {filteredNews.length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Desktop: Sticky Filter Buttons */}
          <section className="hidden md:block py-12 bg-white sticky top-20 z-40 shadow-lg border-b-2 border-gray-300">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-wrap gap-3 justify-center">
                {/* Nút Tất cả */}
                <button
                  onClick={() => handleCategoryChange("all")}
                  className={`group px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                    selectedCategory === "all"
                      ? "bg-[#2a4b7c] text-white shadow-lg shadow-[#2a4b7c]/30 scale-105"
                      : "bg-white text-[#4b5563] hover:bg-gray-50 hover:scale-105 border-2 border-gray-400 hover:border-[#2a4b7c] shadow-sm"
                  }`}
                >
                  <i className="ri-apps-line text-lg"></i>
                  <span>Tất cả</span>
                  {selectedCategory === "all" && (
                    <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                      {newsData.length}
                    </span>
                  )}
                </button>

                {/* Categories từ API */}
                {categories.map((category: any) => (
                  <button
                    key={category._id}
                    onClick={() => handleCategoryChange(category._id, category.slug)}
                    className={`group px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                      selectedCategory === category._id
                        ? "bg-[#2a4b7c] text-white shadow-lg shadow-[#2a4b7c]/30 scale-105"
                        : "bg-white text-[#4b5563] hover:bg-gray-50 hover:scale-105 border-2 border-gray-400 hover:border-[#2a4b7c] shadow-sm"
                    }`}
                  >
                    <i className={`${category.icon || getCategoryIcon(category.slug)} text-lg`}></i>
                    <span>{category.name}</span>
                    {selectedCategory === category._id && (
                      <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                        {filteredNews.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* News Grid - Redesigned */}
      {!loading && !error && (
      <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          {/* Featured News - Large Card */}
          {paginatedNews.length > 0 && (
            <div className={`mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <article className="group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-[#00c897]/50 hover:-translate-y-1">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Image */}
                  <div className="relative h-96 lg:h-auto overflow-hidden">
                    <img
                      src={paginatedNews[0].image}
                      alt={paginatedNews[0].title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-black/10"></div>
                    <div className="absolute top-6 left-6">
                      <span className="px-4 py-2 bg-gradient-to-r from-[#00c897] to-[#00b587] text-white text-sm font-bold rounded-full shadow-lg">
                        Nổi Bật
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-10 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-[#2a4b7c]/10 text-[#2a4b7c] text-sm font-semibold rounded-lg">
                        {paginatedNews[0].newCategory?.name ||
                          paginatedNews[0].newCategoryId?.name}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <i className="ri-time-line"></i>
                        {paginatedNews[0].readTime}
                      </span>
                    </div>

                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 group-hover:text-[#00c897] transition-colors leading-tight">
                      {paginatedNews[0].title}
                    </h2>

                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      {paginatedNews[0].excerpt}
                    </p>

                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-user-line"></i>
                        <span>{paginatedNews[0].author}</span>
                      </div>
                      <span className="text-gray-300">•</span>
                      <div className="text-sm text-gray-500">
                        {new Date(paginatedNews[0].createdAt).toLocaleDateString(
                          "vi-VN"
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-8">
                      {paginatedNews[0].tags.slice(0, 4).map((tag: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-[#00c897] hover:text-white transition-colors cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/tin-tuc/${paginatedNews[0].slug}`}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#00c897] to-[#00b587] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-[#00c897]/30 transition-all duration-300 group-hover:gap-3 w-fit whitespace-nowrap"
                    >
                      Đọc Bài Viết
                      <i className="ri-arrow-right-line text-lg"></i>
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          )}

          {/* Secondary News - 2 Medium Cards */}
          {paginatedNews.length > 1 && (
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {paginatedNews.slice(1, 3).map((news, index) => (
                <article
                  key={news._id}
                  className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-700 border border-gray-100 hover:border-[#00c897]/50 hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${200 + index * 100}ms` }}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-[#2a4b7c] text-sm font-semibold rounded-lg">
                        {news.newCategory?.name || news.newCategoryId?.name}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-2 line-clamp-2 group-hover:text-[#00c897] transition-colors">
                        {news.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                      {news.excerpt}
                    </p>

                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-user-line"></i>
                        <span>{news.author}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <i className="ri-time-line"></i>
                        <span>{news.readTime}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {news.tags.slice(0, 3).map((tag: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/tin-tuc/${news.slug}`}
                      className="inline-flex items-center gap-2 text-[#00c897] font-semibold hover:gap-3 transition-all duration-300 whitespace-nowrap"
                    >
                      Đọc thêm
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Regular News Grid - 3 Columns */}
          {paginatedNews.length > 3 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paginatedNews.slice(3).map((news, index) => (
                <article
                  key={news._id}
                  className={`group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-700 border border-gray-100 hover:border-[#00c897]/30 hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${400 + index * 80}ms` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-white/90 backdrop-blur-sm text-[#2a4b7c] text-xs font-semibold rounded">
                        {news.newCategory?.name || news.newCategoryId?.name}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#00c897] transition-colors leading-tight">
                      {news.title}
                    </h3>

                    <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-2">
                      {news.excerpt}
                    </p>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        <span>{news.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        <span>{news.readTime}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-4">
                      {news.tags.slice(0, 2).map((tag: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/tin-tuc/${news.slug}`}
                      className="inline-flex items-center gap-2 text-[#00c897] font-semibold text-sm hover:gap-3 transition-all duration-300 whitespace-nowrap"
                    >
                      Đọc thêm
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Empty State */}
          {filteredNews.length === 0 && (
            <div className="text-center py-20">
              <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6">
                <i className="ri-file-list-line text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Không tìm thấy tin tức
              </h3>
              <p className="text-gray-600 mb-6">
                Hiện chưa có tin tức nào trong danh mục này
              </p>
              <button
                onClick={() => setSelectedCategory("all")}
                className="px-6 py-3 bg-[#00c897] text-white font-semibold rounded-lg hover:bg-[#00b587] transition-colors whitespace-nowrap"
              >
                Xem tất cả tin tức
              </button>
            </div>
          )}

          {/* Pagination */}
          {filteredNews.length > itemsPerPage && (
            <div className="mt-16 flex justify-center items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => {
                  setCurrentPage((prev) => Math.max(1, prev - 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-[#2a4b7c] border-2 border-[#2a4b7c] hover:bg-[#2a4b7c] hover:text-white shadow-md hover:shadow-lg"
                }`}
              >
                <i className="ri-arrow-left-line"></i>
                <span className="hidden sm:inline">Trước</span>
              </button>

              {/* Page Numbers */}
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => {
                          setCurrentPage(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`w-10 h-10 rounded-lg font-bold transition-all duration-300 ${
                          currentPage === page
                            ? "bg-[#2a4b7c] text-white shadow-lg scale-110"
                            : "bg-white text-[#2a4b7c] border-2 border-gray-200 hover:border-[#2a4b7c] hover:shadow-md"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span key={page} className="w-10 h-10 flex items-center justify-center text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={() => {
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-[#2a4b7c] border-2 border-[#2a4b7c] hover:bg-[#2a4b7c] hover:text-white shadow-md hover:shadow-lg"
                }`}
              >
                <span className="hidden sm:inline">Sau</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-br from-[#2a4b7c] to-[#1e3a5f] relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#00c897] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-[#00c897] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="w-20 h-20 flex items-center justify-center bg-[#00c897] rounded-2xl mx-auto mb-6 shadow-lg shadow-[#00c897]/50">
            <i className="ri-mail-line text-4xl text-white"></i>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Đăng Ký Nhận Tin Mới Nhất
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Nhận thông báo về các bài viết mới nhất về công nghệ, xu hướng và
            kinh nghiệm thực tế
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Nhập email của bạn"
              className="flex-1 px-6 py-4 rounded-lg border-2 border-white/20 bg-white/10 backdrop-blur-sm text-white placeholder-gray-300 focus:outline-none focus:border-[#00c897] transition-colors duration-300"
            />
            <button className="px-8 py-4 bg-[#00c897] text-white font-semibold rounded-lg hover:bg-[#00b587] transition-all duration-300 whitespace-nowrap hover:scale-105 hover:shadow-xl shadow-lg shadow-[#00c897]/30">
              Đăng Ký Ngay
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
